import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Features from "@/pages/Features";
import FreeAudit from "@/pages/FreeAudit";
import AuditReport from "@/pages/AuditReport";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import About from "@/pages/About";
import Pricing from "@/pages/Pricing";
import Customers from "@/pages/Customers";
import Alternative from "@/pages/Alternative";
import Subscribe from "@/pages/Subscribe";
import AssessmentConfirmation from "@/pages/AssessmentConfirmation";
import DashboardLayout from "@/components/DashboardLayout";
import Overview from "@/pages/dashboard/Overview";
import Budgets from "@/pages/dashboard/Budgets";
import Vendors from "@/pages/dashboard/Vendors";
import Leads from "@/pages/dashboard/Leads";
import Invoices from "@/pages/dashboard/Invoices";
import Scorecards from "@/pages/dashboard/Scorecards";
import Compliance from "@/pages/dashboard/Compliance";
import ExecutiveReport from "@/pages/dashboard/ExecutiveReport";
import AlertsDigest from "@/pages/dashboard/AlertsDigest";
import Billing from "@/pages/dashboard/Billing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import { PlanGate } from "@/components/PlanGate";
import AcceptInvite from "@/pages/AcceptInvite";
import Team from "@/pages/dashboard/Team";
import Inquiries from "@/pages/dashboard/Inquiries";
import Experiments from "@/pages/dashboard/Experiments";
import SalesPlaybook from "@/pages/dashboard/SalesPlaybook";
import Security from "@/pages/dashboard/Security";
import Governance from "@/pages/dashboard/Governance";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Terms from "@/pages/Terms";
import SecurityTrust from "@/pages/SecurityTrust";
import RoiShowcase from "@/pages/RoiShowcase";
import MarketingAuditGuide from "@/pages/MarketingAuditGuide";
import CostPerSignedCase from "@/pages/CostPerSignedCase";
import Insights from "@/pages/Insights";
import InsightArticle from "@/pages/InsightArticle";
import Deck from "@/pages/Deck";
import PartnerReferral from "@/pages/PartnerReferral";
import { PermGate } from "@/components/PermGate";
import { AdminGate } from "@/components/AdminGate";
import Integrations from "@/pages/dashboard/Integrations";
import Resources from "@/pages/dashboard/Resources";
import FirmProfile from "@/pages/dashboard/FirmProfile";
import BaselineReview from "@/pages/BaselineReview";
import BaselineConfirmation from "@/pages/BaselineConfirmation";
import BaselineIntake from "@/pages/BaselineIntake";
import BaselineAdmin from "@/pages/dashboard/BaselineAdmin";

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F1EA] font-mono-data text-sm text-zinc-500">
        Loading…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PaidRoute({ children }) {
  const { user } = useAuth();
  if (!user) return null;
  const active = ["active", "trialing", "past_due"].includes(user?.subscription_status);
  const allowed = active || user?.is_platform_admin || user?.is_demo || user?.access_comped;
  if (!allowed) return <Navigate to="/subscribe" replace />;
  return children;
}

function App() {
  useEffect(() => {
    document.documentElement.classList.remove("dark");
  }, []);

  return (
    <HelmetProvider>
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/features" element={<Features />} />
            <Route path="/about" element={<About />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/vs" element={<Alternative />} />
            <Route path="/law-firm-marketing-audit" element={<MarketingAuditGuide />} />
            <Route path="/cost-per-signed-case" element={<CostPerSignedCase />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/insights/:slug" element={<InsightArticle />} />
            <Route path="/deck" element={<Deck />} />
            <Route path="/deck/:type" element={<Deck />} />
            <Route path="/refer" element={<PartnerReferral />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/security" element={<SecurityTrust />} />
            <Route path="/roi-showcase" element={<RoiShowcase />} />
            <Route path="/consultation" element={<FreeAudit />} />
            <Route path="/free-audit" element={<Navigate to="/consultation" replace />} />
            <Route path="/audit/:token" element={<AuditReport />} />
            <Route path="/baseline-review" element={<BaselineReview />} />
            <Route path="/baseline-review-confirmation" element={<BaselineConfirmation />} />
            <Route path="/baseline-intake/:token" element={<BaselineIntake />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/assessment-confirmation" element={<AssessmentConfirmation />} />
            <Route path="/subscribe" element={<ProtectedRoute><Subscribe /></ProtectedRoute>} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route
              path="/payment/success"
              element={
                <ProtectedRoute>
                  <PaymentSuccess />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment/cancel"
              element={
                <ProtectedRoute>
                  <PaymentCancel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <PaidRoute>
                    <DashboardLayout />
                  </PaidRoute>
                </ProtectedRoute>
              }
            >
              <Route index element={<Overview />} />
              <Route path="resources" element={<Resources />} />
              <Route path="profile" element={<FirmProfile />} />
              <Route path="budgets" element={<Budgets />} />
              <Route path="vendors" element={<Vendors />} />
              <Route path="leads" element={<Leads />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="scorecards" element={<PlanGate feature="scorecards" title="Vendor Scorecards"><Scorecards /></PlanGate>} />
              <Route path="compliance" element={<PlanGate feature="compliance" title="Compliance Archive"><Compliance /></PlanGate>} />
              <Route path="alerts" element={<PlanGate feature="alerts" title="Alerts & Weekly Digest"><AlertsDigest /></PlanGate>} />
              <Route path="report" element={<ExecutiveReport />} />
              <Route path="integrations" element={<PermGate action="integration:manage" title="Integrations"><Integrations /></PermGate>} />
              <Route path="billing" element={<PermGate action="billing:manage" title="Billing & Plans"><Billing /></PermGate>} />
              <Route path="team" element={<PermGate action="user:manage" title="Team & Roles"><Team /></PermGate>} />
              <Route path="inquiries" element={<AdminGate title="Leads & Inquiries"><Inquiries /></AdminGate>} />
              <Route path="experiments" element={<AdminGate title="A/B Experiments"><Experiments /></AdminGate>} />
              <Route path="playbook" element={<AdminGate title="Sales Playbook"><SalesPlaybook /></AdminGate>} />
              <Route path="baseline" element={<AdminGate title="Baseline Campaign"><BaselineAdmin /></AdminGate>} />
              <Route path="security" element={<Security />} />
              <Route path="governance" element={<PermGate action="user:manage" title="Audit & Data"><Governance /></PermGate>} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </AuthProvider>
    </div>
    </HelmetProvider>
  );
}

export default App;
